/**
 * locations.js — the place list behind the location picker.
 *
 * WHY THE UK IS SO MUCH MORE DETAILED
 *
 * Location feeds the AI briefing's demographic weighting. "London" is useless
 * for that: Kensington and Newham sit four miles apart and differ enormously in
 * deprivation, ethnic composition, age structure and disease prevalence. A GP
 * in Newham wants the diabetes and TB literature; a GP in Richmond does not, to
 * anything like the same degree.
 *
 * So UK entries go down to borough and town level, and each carries a `context`
 * string — a short, factual note the briefing function passes to the model so
 * it can weight papers by population fit. Keep those notes descriptive and
 * demographic, never clinical claims.
 *
 * International entries are city-level only. Clincordant's first users are UK
 * clinicians; adding this much granularity for forty countries would be a lot
 * of unverified data for very little benefit. Expand a country when you have
 * users there.
 *
 * `type` drives the grouping label in the dropdown.
 */

/* ── London ────────────────────────────────────────────────────────────────
   All 32 boroughs plus the City. Split by broad character, because that is
   what actually predicts the case mix a clinician sees.
   ──────────────────────────────────────────────────────────────────────── */

const LONDON = [
  ['Barking and Dagenham', 'High deprivation, young population, large Black African and Asian communities'],
  ['Barnet', 'Large Jewish and South Asian communities, ageing in the north of the borough'],
  ['Bexley', 'Older, predominantly White British, outer suburban'],
  ['Brent', 'Among the most ethnically diverse in the UK, large South Asian population, high TB incidence'],
  ['Bromley', 'Older, affluent outer suburban, large elderly population'],
  ['Camden', 'Sharp deprivation contrasts, large student and homeless populations'],
  ['City of London', 'Very small resident population, large daytime commuter population'],
  ['Croydon', 'Ethnically diverse, mixed deprivation, large young population'],
  ['Ealing', 'Large South Asian and Eastern European communities'],
  ['Enfield', 'Diverse, significant Turkish and Kurdish communities, mixed deprivation'],
  ['Greenwich', 'Mixed deprivation, growing young population, large Nepali community'],
  ['Hackney', 'High deprivation alongside rapid gentrification, large Orthodox Jewish community'],
  ['Hammersmith and Fulham', 'Affluent with pockets of high deprivation'],
  ['Haringey', 'Stark east–west deprivation divide, very ethnically diverse'],
  ['Harrow', 'Largest Indian and Sri Lankan Tamil populations in London, high diabetes prevalence'],
  ['Havering', 'Older, predominantly White British, outer east London'],
  ['Hillingdon', 'Mixed, includes Heathrow, large South Asian population'],
  ['Hounslow', 'Very large South Asian population, high cardiometabolic risk'],
  ['Islington', 'High deprivation despite affluence nearby, dense, high mental health need'],
  ['Kensington and Chelsea', 'Widest income inequality in the UK, high life expectancy overall'],
  ['Kingston upon Thames', 'Affluent, large Korean and Tamil communities'],
  ['Lambeth', 'Large Black Caribbean and African populations, high sickle cell and HIV prevalence'],
  ['Lewisham', 'Diverse, mixed deprivation, large Black African and Caribbean populations'],
  ['Merton', 'Mixed, growing South Asian and Eastern European populations'],
  ['Newham', 'Among the most deprived and most diverse in England, very young, high TB and diabetes'],
  ['Redbridge', 'Large South Asian population, high type 2 diabetes prevalence'],
  ['Richmond upon Thames', 'Affluent, older, low deprivation'],
  ['Southwark', 'Sharp deprivation contrasts, large Black African population'],
  ['Sutton', 'Older, suburban, relatively low deprivation'],
  ['Tower Hamlets', 'Highest child poverty in the UK, very large Bangladeshi community, young population'],
  ['Waltham Forest', 'Diverse, high deprivation in the south of the borough'],
  ['Wandsworth', 'Affluent, young professional, low deprivation'],
  ['Westminster', 'Extreme inequality, large migrant and homeless populations'],
].map(([name, context]) => ({
  name, context, region: 'London', country: 'United Kingdom', type: 'London borough',
}));

/* ── English cities and large towns ──────────────────────────────────────── */

const ENGLAND_CITIES = [
  ['Manchester', 'North West', 'High deprivation, young population, large student body, significant health inequality'],
  ['Salford', 'North West', 'High deprivation, lower than average life expectancy'],
  ['Liverpool', 'North West', 'Among the most deprived English cities, high smoking-related disease'],
  ['Bolton', 'North West', 'Large South Asian population, high cardiometabolic risk'],
  ['Blackburn', 'North West', 'Very large South Asian population, high deprivation and diabetes prevalence'],
  ['Blackpool', 'North West', 'Lowest male life expectancy in England, very high deprivation and substance misuse'],
  ['Preston', 'North West', 'Mixed, significant South Asian population'],
  ['Oldham', 'North West', 'High deprivation, large Bangladeshi and Pakistani communities'],
  ['Rochdale', 'North West', 'High deprivation, large South Asian population'],
  ['Stockport', 'North West', 'Mixed affluence, ageing population'],
  ['Wigan', 'North West', 'Post-industrial, high smoking and COPD prevalence'],
  ['Warrington', 'North West', 'Relatively affluent, mixed'],
  ['Chester', 'North West', 'Affluent, older population'],
  ['Lancaster', 'North West', 'University town, mixed rural catchment'],
  ['Carlisle', 'North West', 'Rural catchment, ageing, remote access issues'],
  ['Barrow-in-Furness', 'North West', 'Isolated, high deprivation, industrial'],

  ['Leeds', 'Yorkshire', 'Large and mixed, significant deprivation alongside affluence'],
  ['Sheffield', 'Yorkshire', 'Post-industrial, sharp east–west health divide, large student population'],
  ['Bradford', 'Yorkshire', 'Very large Pakistani community, high consanguinity, high diabetes and infant mortality'],
  ['Hull', 'Yorkshire', 'Among the most deprived in England, high smoking and cardiovascular disease'],
  ['York', 'Yorkshire', 'Affluent, older, low deprivation'],
  ['Wakefield', 'Yorkshire', 'Post-industrial, high COPD prevalence'],
  ['Huddersfield', 'Yorkshire', 'Mixed, significant South Asian population'],
  ['Doncaster', 'Yorkshire', 'Post-industrial, high deprivation'],
  ['Middlesbrough', 'North East', 'Among the most deprived in England, very low healthy life expectancy'],
  ['Newcastle upon Tyne', 'North East', 'Large student population, sharp deprivation gradient'],
  ['Sunderland', 'North East', 'Post-industrial, high deprivation and respiratory disease'],
  ['Gateshead', 'North East', 'Post-industrial, high deprivation'],
  ['Durham', 'North East', 'University town with a deprived former-mining catchment'],
  ['Darlington', 'North East', 'Mixed, market town serving a rural area'],

  ['Birmingham', 'West Midlands', 'Youngest major UK city, very ethnically diverse, high diabetes and deprivation'],
  ['Coventry', 'West Midlands', 'Diverse, large student and migrant populations'],
  ['Wolverhampton', 'West Midlands', 'High deprivation, large South Asian and Black Caribbean populations'],
  ['Walsall', 'West Midlands', 'High deprivation, mixed ethnicity'],
  ['Dudley', 'West Midlands', 'Post-industrial, ageing'],
  ['Stoke-on-Trent', 'West Midlands', 'Very high deprivation, high smoking prevalence'],
  ['Telford', 'West Midlands', 'New town, mixed deprivation'],
  ['Shrewsbury', 'West Midlands', 'Rural catchment, ageing population'],
  ['Worcester', 'West Midlands', 'Market town, mixed rural catchment'],
  ['Hereford', 'West Midlands', 'Very rural catchment, ageing, access difficulties'],

  ['Nottingham', 'East Midlands', 'High deprivation, very large student population'],
  ['Leicester', 'East Midlands', 'Majority-minority city, very large Indian population, high diabetes prevalence'],
  ['Derby', 'East Midlands', 'Diverse, manufacturing base, mixed deprivation'],
  ['Northampton', 'East Midlands', 'Growing, mixed'],
  ['Lincoln', 'East Midlands', 'Large rural catchment, ageing, seasonal agricultural workforce'],
  ['Chesterfield', 'East Midlands', 'Former mining area, high COPD prevalence'],

  ['Norwich', 'East of England', 'Large rural catchment, ageing population'],
  ['Ipswich', 'East of England', 'Mixed, growing Eastern European population'],
  ['Cambridge', 'East of England', 'Highly educated, affluent, large research and student population'],
  ['Peterborough', 'East of England', 'High deprivation, large Eastern European population'],
  ['Colchester', 'East of England', 'Garrison town, mixed'],
  ['Chelmsford', 'East of England', 'Affluent commuter belt'],
  ['Southend-on-Sea', 'East of England', 'Coastal, ageing, pockets of high deprivation'],
  ['Luton', 'East of England', 'Very diverse, large Pakistani and Bangladeshi communities, high deprivation'],
  ['Bedford', 'East of England', 'Diverse, large Italian and South Asian communities'],
  ['Watford', 'East of England', 'Diverse commuter town'],
  ['St Albans', 'East of England', 'Affluent commuter belt'],
  ['Milton Keynes', 'South East', 'Fast-growing new town, young population'],

  ['Oxford', 'South East', 'Highly educated, large student population, affluent with deprived estates'],
  ['Reading', 'South East', 'Affluent, diverse, large commuter population'],
  ['Brighton and Hove', 'South East', 'Large LGBTQ+ population, high HIV prevalence, significant homelessness'],
  ['Southampton', 'South East', 'Port city, large student population, mixed deprivation'],
  ['Portsmouth', 'South East', 'Dense, naval, high deprivation in central areas'],
  ['Canterbury', 'South East', 'University town, large rural and coastal catchment'],
  ['Maidstone', 'South East', 'County town, mixed rural catchment'],
  ['Guildford', 'South East', 'Affluent, older'],
  ['Crawley', 'South East', 'Airport-linked, diverse, mixed deprivation'],
  ['Slough', 'South East', 'Among the most ethnically diverse in the UK, very high diabetes prevalence'],
  ['High Wycombe', 'South East', 'Diverse, large Pakistani community'],
  ['Basingstoke', 'South East', 'Commuter and light industrial'],
  ['Hastings', 'South East', 'Coastal, very high deprivation, ageing'],
  ['Margate', 'South East', 'Coastal, high deprivation, ageing'],

  ['Bristol', 'South West', 'Large and diverse, sharp deprivation divide, large student population'],
  ['Bath', 'South West', 'Affluent, large student population, ageing residents'],
  ['Plymouth', 'South West', 'Naval, isolated from tertiary centres, mixed deprivation'],
  ['Exeter', 'South West', 'Serves a very large rural catchment, ageing'],
  ['Gloucester', 'South West', 'Mixed, growing diversity'],
  ['Cheltenham', 'South West', 'Affluent, older'],
  ['Swindon', 'South West', 'Manufacturing base, mixed'],
  ['Bournemouth', 'South West', 'Coastal, very large retired population, seasonal students'],
  ['Poole', 'South West', 'Coastal, ageing, affluent'],
  ['Torquay', 'South West', 'Coastal, among the oldest populations in England, high deprivation'],
  ['Truro', 'South West', 'Serves all of Cornwall, extreme rurality and access difficulty'],
].map(([name, region, context]) => ({
  name, region, context, country: 'United Kingdom', type: 'City or town',
}));

/* ── Devolved nations ────────────────────────────────────────────────────── */

const SCOTLAND = [
  ['Edinburgh', 'Affluent overall with deprived peripheral estates, large student population'],
  ['Glasgow', 'Among the worst health outcomes in Western Europe, extreme deprivation, high alcohol and drug mortality'],
  ['Aberdeen', 'Oil economy, relatively affluent, large transient workforce'],
  ['Dundee', 'High deprivation, highest drug-related death rate in Europe'],
  ['Inverness', 'Serves the entire Highlands, extreme rurality and transport difficulty'],
  ['Stirling', 'Mixed urban and rural catchment'],
  ['Perth', 'Rural catchment, ageing'],
  ['Paisley', 'High deprivation, post-industrial'],
  ['Ayr', 'Coastal, ageing, mixed deprivation'],
  ['Dumfries', 'Very rural, ageing, long travel times to tertiary care'],
  ['Fort William', 'Remote and rural, small population, significant access issues'],
  ['Kirkwall (Orkney)', 'Island community, extreme remoteness, ageing'],
  ['Lerwick (Shetland)', 'Island community, extreme remoteness'],
  ['Stornoway (Western Isles)', 'Island community, Gaelic-speaking, extreme remoteness'],
].map(([name, context]) => ({
  name, context, region: 'Scotland', country: 'United Kingdom', type: 'City or town',
}));

const WALES = [
  ['Cardiff', 'Largest Welsh city, diverse, sharp north–south deprivation divide'],
  ['Swansea', 'Post-industrial, large student population'],
  ['Newport', 'Post-industrial, high deprivation, growing diversity'],
  ['Wrexham', 'North Wales, serves a large rural catchment'],
  ['Bangor', 'North Wales, Welsh-speaking, very rural catchment'],
  ['Aberystwyth', 'Mid Wales, extremely rural, long distances to tertiary care'],
  ['Merthyr Tydfil', 'Among the most deprived areas in the UK, very high rates of chronic disease'],
  ['Rhondda Cynon Taf', 'Former mining valleys, high deprivation and respiratory disease'],
  ['Carmarthen', 'West Wales, rural, Welsh-speaking, ageing'],
  ['Haverfordwest', 'Pembrokeshire, very rural and coastal, ageing'],
].map(([name, context]) => ({
  name, context, region: 'Wales', country: 'United Kingdom', type: 'City or town',
}));

const NORTHERN_IRELAND = [
  ['Belfast', 'Sharp sectarian and deprivation divides, high mental health need and suicide rates'],
  ['Londonderry / Derry', 'High deprivation, border catchment'],
  ['Lisburn', 'Commuter, relatively affluent'],
  ['Newry', 'Border town, cross-border patient flows'],
  ['Ballymena', 'Rural catchment, market town'],
  ['Omagh', 'Very rural, long travel times'],
  ['Enniskillen', 'Very rural, border catchment'],
].map(([name, context]) => ({
  name, context, region: 'Northern Ireland', country: 'United Kingdom', type: 'City or town',
}));

/* ── UK counties and rural areas ─────────────────────────────────────────
   For clinicians whose catchment is genuinely a county rather than a town.
   ──────────────────────────────────────────────────────────────────────── */

const UK_COUNTIES = [
  ['Cornwall', 'South West', 'Extreme rurality, oldest population in England, severe seasonal tourist surge'],
  ['Devon', 'South West', 'Large rural and coastal, ageing, long travel times'],
  ['Somerset', 'South West', 'Rural, ageing'],
  ['Dorset', 'South West', 'Coastal and rural, very large retired population'],
  ['Wiltshire', 'South West', 'Rural, military population'],
  ['Norfolk', 'East of England', 'Very rural, ageing, coastal deprivation, seasonal agricultural workers'],
  ['Suffolk', 'East of England', 'Rural and coastal, ageing'],
  ['Lincolnshire', 'East Midlands', 'Very rural, large migrant agricultural workforce, access difficulties'],
  ['Cumbria', 'North West', 'Very rural, ageing, extreme travel times to tertiary care'],
  ['Northumberland', 'North East', 'Most sparsely populated English county, significant access issues'],
  ['North Yorkshire', 'Yorkshire', 'Very rural, ageing, large geographic spread'],
  ['Shropshire', 'West Midlands', 'Rural, ageing'],
  ['Herefordshire', 'West Midlands', 'Very rural, ageing, seasonal agricultural workforce'],
  ['Kent', 'South East', 'Mixed, coastal deprivation, cross-Channel port health considerations'],
  ['Essex', 'East of England', 'Mixed urban, commuter and coastal'],
  ['Hampshire', 'South East', 'Mixed, military population'],
  ['Isle of Wight', 'South East', 'Island, oldest population in England, isolated from tertiary services'],
  ['Isles of Scilly', 'South West', 'Extremely remote island community, tiny population'],
].map(([name, region, context]) => ({
  name, region, context, country: 'United Kingdom', type: 'County or rural area',
}));

/* ── International — city level only ─────────────────────────────────────── */

const INTERNATIONAL = [
  ['Dublin', 'Ireland'], ['Cork', 'Ireland'], ['Galway', 'Ireland'],
  ['Paris', 'France'], ['Lyon', 'France'], ['Marseille', 'France'],
  ['Berlin', 'Germany'], ['Munich', 'Germany'], ['Hamburg', 'Germany'], ['Frankfurt', 'Germany'],
  ['Amsterdam', 'Netherlands'], ['Rotterdam', 'Netherlands'], ['Utrecht', 'Netherlands'],
  ['Brussels', 'Belgium'], ['Antwerp', 'Belgium'],
  ['Madrid', 'Spain'], ['Barcelona', 'Spain'], ['Valencia', 'Spain'],
  ['Rome', 'Italy'], ['Milan', 'Italy'], ['Naples', 'Italy'],
  ['Lisbon', 'Portugal'], ['Porto', 'Portugal'],
  ['Zurich', 'Switzerland'], ['Geneva', 'Switzerland'], ['Basel', 'Switzerland'],
  ['Vienna', 'Austria'], ['Stockholm', 'Sweden'], ['Gothenburg', 'Sweden'],
  ['Copenhagen', 'Denmark'], ['Oslo', 'Norway'], ['Helsinki', 'Finland'],
  ['Warsaw', 'Poland'], ['Krakow', 'Poland'], ['Prague', 'Czechia'],
  ['Budapest', 'Hungary'], ['Athens', 'Greece'],

  ['New York', 'United States'], ['Boston', 'United States'], ['Philadelphia', 'United States'],
  ['Baltimore', 'United States'], ['Washington DC', 'United States'], ['Atlanta', 'United States'],
  ['Miami', 'United States'], ['Chicago', 'United States'], ['Houston', 'United States'],
  ['Dallas', 'United States'], ['Denver', 'United States'], ['Seattle', 'United States'],
  ['San Francisco', 'United States'], ['Los Angeles', 'United States'], ['San Diego', 'United States'],
  ['Rochester MN', 'United States'], ['Cleveland', 'United States'], ['Ann Arbor', 'United States'],

  ['Toronto', 'Canada'], ['Vancouver', 'Canada'], ['Montreal', 'Canada'],
  ['Calgary', 'Canada'], ['Ottawa', 'Canada'], ['Edmonton', 'Canada'], ['Halifax', 'Canada'],

  ['Sydney', 'Australia'], ['Melbourne', 'Australia'], ['Brisbane', 'Australia'],
  ['Perth', 'Australia'], ['Adelaide', 'Australia'], ['Canberra', 'Australia'], ['Hobart', 'Australia'],
  ['Auckland', 'New Zealand'], ['Wellington', 'New Zealand'], ['Christchurch', 'New Zealand'],

  ['Singapore', 'Singapore'], ['Hong Kong', 'Hong Kong SAR'],
  ['Tokyo', 'Japan'], ['Osaka', 'Japan'], ['Seoul', 'South Korea'],
  ['Mumbai', 'India'], ['Delhi', 'India'], ['Bengaluru', 'India'],
  ['Chennai', 'India'], ['Hyderabad', 'India'], ['Kolkata', 'India'],
  ['Karachi', 'Pakistan'], ['Lahore', 'Pakistan'], ['Islamabad', 'Pakistan'],
  ['Dhaka', 'Bangladesh'], ['Colombo', 'Sri Lanka'], ['Kathmandu', 'Nepal'],
  ['Kuala Lumpur', 'Malaysia'], ['Bangkok', 'Thailand'], ['Manila', 'Philippines'],
  ['Jakarta', 'Indonesia'],

  ['Dubai', 'United Arab Emirates'], ['Abu Dhabi', 'United Arab Emirates'],
  ['Doha', 'Qatar'], ['Riyadh', 'Saudi Arabia'], ['Jeddah', 'Saudi Arabia'],
  ['Tel Aviv', 'Israel'], ['Amman', 'Jordan'],

  ['Cape Town', 'South Africa'], ['Johannesburg', 'South Africa'], ['Durban', 'South Africa'],
  ['Nairobi', 'Kenya'], ['Lagos', 'Nigeria'], ['Abuja', 'Nigeria'],
  ['Accra', 'Ghana'], ['Cairo', 'Egypt'], ['Kampala', 'Uganda'], ['Dar es Salaam', 'Tanzania'],

  ['São Paulo', 'Brazil'], ['Rio de Janeiro', 'Brazil'],
  ['Buenos Aires', 'Argentina'], ['Santiago', 'Chile'], ['Mexico City', 'Mexico'], ['Bogotá', 'Colombia'],
].map(([name, country]) => ({
  name, country, region: country, context: '', type: 'International',
}));

/* ── Assembled ───────────────────────────────────────────────────────────── */

export const LOCATIONS = [
  ...LONDON,
  ...ENGLAND_CITIES,
  ...SCOTLAND,
  ...WALES,
  ...NORTHERN_IRELAND,
  ...UK_COUNTIES,
  ...INTERNATIONAL,
];

/** "Islington, London" / "Truro, South West" / "Toronto, Canada" */
export function locationLabel(loc) {
  if (!loc) return '';
  if (loc.country !== 'United Kingdom') return `${loc.name}, ${loc.country}`;
  return loc.region === loc.name ? loc.name : `${loc.name}, ${loc.region}`;
}

/** Strip accents and case so "sao paulo" finds "São Paulo". */
function normalise(str) {
  return String(str)
    .normalize('NFD')
    // Combining diacritical marks, written as escapes rather than literal
    // characters so the source file stays ASCII and cannot be corrupted by an
    // editor or a copy-paste. Same lesson as functions/lib/guards.js.
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '')
    .toLowerCase()
    .trim();
}

/**
 * Search, ranked so exact and prefix matches beat mid-word ones.
 *
 * Without the ranking, typing "man" surfaces "Isle of Man"-style incidental
 * matches above Manchester, which makes the picker feel broken.
 */
export function searchLocations(query, limit = 40) {
  const q = normalise(query);

  // No query: show a useful starting set rather than an arbitrary alphabetical
  // slice — London boroughs and the largest cities are what most users want.
  if (!q) {
    return [
      ...LONDON.filter((l) => ['Islington', 'Camden', 'Tower Hamlets', 'Lambeth', 'Newham'].includes(l.name)),
      ...ENGLAND_CITIES.filter((l) =>
        ['Manchester', 'Birmingham', 'Leeds', 'Liverpool', 'Bristol', 'Sheffield', 'Newcastle upon Tyne'].includes(l.name)),
      ...SCOTLAND.filter((l) => ['Edinburgh', 'Glasgow'].includes(l.name)),
      ...WALES.filter((l) => l.name === 'Cardiff'),
      ...NORTHERN_IRELAND.filter((l) => l.name === 'Belfast'),
    ];
  }

  const scored = [];
  for (const loc of LOCATIONS) {
    const name = normalise(loc.name);
    const region = normalise(loc.region);
    const country = normalise(loc.country);

    let score = 0;
    if (name === q) score = 100;
    else if (name.startsWith(q)) score = 80;
    else if (name.includes(q)) score = 50;
    else if (region.startsWith(q)) score = 30;
    else if (country.startsWith(q)) score = 20;
    else if (region.includes(q) || country.includes(q)) score = 10;

    // Nudge UK entries up: they are the detailed ones and the intended audience.
    if (score > 0) {
      if (loc.country === 'United Kingdom') score += 5;
      scored.push({ loc, score });
    }
  }

  return scored
    .sort((a, b) => b.score - a.score || a.loc.name.localeCompare(b.loc.name))
    .slice(0, limit)
    .map((s) => s.loc);
}

/** Resolve a stored label back to a full record, for editing an existing profile. */
export function findLocation(label) {
  if (!label) return null;
  const target = normalise(label);
  return (
    LOCATIONS.find((l) => normalise(locationLabel(l)) === target) ||
    LOCATIONS.find((l) => normalise(l.name) === target) ||
    null
  );
}
